import { useSignal } from '@preact/signals';

import type { DinnerChoiceValue } from '@amumin/api-client';
import { interpolate } from '@amumin/i18n';
import { SegmentedControl, showToast } from '@amumin/ui';

import { api } from '../api.js';
import { Panel, WidgetHead } from '../home/HomeWidgets.js';
import { hhmmIn } from '../lib/dates.js';
import { Skeleton, toastError, useLoad } from '../lib/ui.js';
import { appText, bumpData, ownMemberId } from '../state/session.js';
import { euro, nameOf, samenTz, samenWords } from './common.js';

// Vandaag-widgets van Samen wonen (golf 8, §2.2.4): Vanavond eten (de eigen knop en het aantal
// eters), Reserveren (de eigen volgende reservering en een regel vrij/bezet per ding) en Kosten
// (alleen het eigen saldo, alleen volwassenen). Een widget van een slapende module rendert niets.

export function DinnerWidget({ tick }: { tick: number }) {
  const a = appText.value;
  const d = a.samen.dinner;
  const me = ownMemberId.value;
  const busy = useSignal(false);
  const load = useLoad(() => api.samen.dinner.overview(undefined, 7), [tick]);
  const data = load.data.value;
  if (load.error.value && !data) return null;
  if (data && !data.settings) return null;
  const day = data?.days.find((x) => x.date === data.today);
  if (data && (!day || !day.dinner_day)) return null;
  const mine = day?.rows.find((r) => r.member_id === me);
  const cookId = day?.cook?.member_id ?? null;
  const value: DinnerChoiceValue | 'none' =
    mine && mine.source === 'explicit' && mine.status !== 'unknown' ? mine.status : 'none';
  const set = async (v: DinnerChoiceValue | 'none') => {
    if (!me || !day || busy.value) return;
    busy.value = true;
    try {
      load.data.value = await api.samen.dinner.signup(day.date, me, v === 'none' ? null : v);
      bumpData();
      showToast({
        kind: 'success',
        message:
          v === 'in'
            ? interpolate(d.signedIn, { name: nameOf(me) })
            : v === 'out'
              ? interpolate(d.signedOut, { name: nameOf(me) })
              : d.cleared,
      });
    } catch (e) {
      toastError(e);
    } finally {
      busy.value = false;
    }
  };
  return (
    <Panel name="widget-dinner" labelledBy="w-dinner-title">
      <WidgetHead
        id="w-dinner-title"
        title={samenWords.value.dinnerTitle}
        href="/wie-eet-mee"
        link={d.widgetAll}
      />
      {!data || !day ? (
        <Skeleton rows={2} />
      ) : day.no_dinner ? (
        <p class="muted small">{d.noDinner}</p>
      ) : (
        <>
          <p class="small" data-name="widget-dinner-head">
            <strong>{d.tonight}</strong>
            {' · '}
            {cookId ? interpolate(d.headCook, { name: nameOf(cookId) }) : d.headNoCook}
            {' · '}
            {interpolate(d.eaters, { n: day.counts.in })}
            {day.counts.guests > 0
              ? ` ${interpolate(d.guestsExtra, { n: day.counts.guests })}`
              : ''}
          </p>
          {me && day.open ? (
            <SegmentedControl<DinnerChoiceValue | 'none'>
              label={d.myChoice}
              name="widget-dinner-choice"
              size="small"
              value={value}
              onChange={(v) => void set(v)}
              options={[
                { value: 'in', label: d.imIn },
                { value: 'out', label: d.imOut },
                { value: 'none', label: d.noChoice },
              ]}
            />
          ) : (
            <p class="muted small">
              {day.closed
                ? cookId
                  ? interpolate(d.closed, { name: nameOf(cookId) })
                  : d.closedNoCook
                : mine?.status === 'in'
                  ? d.widgetYouIn
                  : mine?.status === 'out'
                    ? d.widgetYouOut
                    : d.widgetNotYet}
            </p>
          )}
        </>
      )}
    </Panel>
  );
}

export function BookingsWidget({ tick }: { tick: number }) {
  const a = appText.value;
  const b = a.samen.bookings;
  const tz = samenTz.value;
  const me = ownMemberId.value;
  const load = useLoad(() => api.samen.bookings.overview(), [tick]);
  const data = load.data.value;
  if (load.error.value && !data) return null;
  if (data && data.resources.length === 0) return null;
  const now = Date.now();
  const live = (data?.bookings ?? []).filter((x) => !x.cancelled_at);
  const next = live
    .filter((x) => x.member_id === me && Date.parse(x.ends_at) > now)
    .sort((x, y) => x.starts_at.localeCompare(y.starts_at))[0];
  const resourceName = (id: string) => data?.resources.find((r) => r.id === id)?.name ?? '';
  return (
    <Panel name="widget-bookings" labelledBy="w-bookings-title">
      <WidgetHead
        id="w-bookings-title"
        title={a.home.widgets.bookings.name}
        href="/reserveren"
        link={b.widgetAll}
      />
      {!data ? (
        <Skeleton rows={2} />
      ) : (
        <>
          {next ? (
            <p class="small" data-name="widget-bookings-next">
              <strong>
                {interpolate(b.widgetNext, {
                  resource: resourceName(next.resource_id),
                  time: `${hhmmIn(next.starts_at, tz)}–${hhmmIn(next.ends_at, tz)}`,
                })}
              </strong>
            </p>
          ) : null}
          <ul class="widget-chips" data-name="widget-bookings-now">
            {data.resources
              .filter((r) => r.unit === 'slot')
              .slice(0, 3)
              .map((r) => {
                const busyNow = live.find(
                  (x) =>
                    x.resource_id === r.id &&
                    Date.parse(x.starts_at) <= now &&
                    Date.parse(x.ends_at) > now,
                );
                return (
                  <li key={r.id} class="widget-chip">
                    <span>
                      {busyNow
                        ? interpolate(b.widgetBusyUntil, {
                            resource: r.name,
                            time: hhmmIn(busyNow.ends_at, tz),
                          })
                        : interpolate(b.widgetFreeNow, { resource: r.name })}
                    </span>
                  </li>
                );
              })}
          </ul>
        </>
      )}
    </Panel>
  );
}

export function BalanceWidget({ tick }: { tick: number }) {
  const a = appText.value;
  const c = a.samen.costs;
  const me = ownMemberId.value;
  const load = useLoad(() => api.samen.expenses.balances(), [tick]);
  const data = load.data.value;
  if (load.error.value && !data) return null;
  const mine = data?.balances.find((x) => x.member_id === me);
  return (
    <Panel name="widget-balance" labelledBy="w-balance-title">
      <WidgetHead
        id="w-balance-title"
        title={samenWords.value.costsTitle}
        href="/kosten"
        link={c.widgetAll}
      />
      {!data ? (
        <Skeleton rows={1} />
      ) : (
        <p class="small" data-name="widget-balance-mine">
          {!mine || mine.cents === 0
            ? c.widgetEven
            : mine.cents > 0
              ? interpolate(c.widgetGets, { amount: euro(mine.cents) })
              : interpolate(c.widgetOwes, { amount: euro(-mine.cents) })}
        </p>
      )}
    </Panel>
  );
}
